import React from 'react';
import { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { AppTextField, AppTextareaField } from '../../../../../../Components/FormKit';

const cardHeader = (title) => (
    <div style={{
        background: '#f8fafc',
        padding: '0.65rem 1rem',
        borderBottom: '1px solid #e2e8f0',
        borderRadius: '14px 14px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    }}>
        <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
            {title}
        </span>
        <div style={{ flex: 1, height: '1px', background: '#bfdbfe' }} />
    </div>
);

export const StepCompanyEmail = ({
    // currentStatus unused here — kept for API parity with OcularVisitModal
    companyRecipientName,
    setCompanyRecipientName,
    companyEmail,
    setCompanyEmail,
    companyCcEmails = [],
    setCompanyCcEmails,
    companyEmailSubject,
    setCompanyEmailSubject,
    companyEmailContent,
    setCompanyEmailContent,
    isReadOnly,
    setCompanyUploadedFiles,
    stepData,
    defaultAttachments = [],
    excludedDefaultPaths = [],
    onToggleDefaultPath,
    errors = {}
}) => {
    const inputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [previewAttachment, setPreviewAttachment] = useState(null);

    // Prioriza la prop (endpoint dedicado), con fallback al stepData
    const rawSystemAttachments = (defaultAttachments && defaultAttachments.length > 0)
        ? defaultAttachments
        : (stepData?.default_attachments || []);
    // Filtramos los removidos por el admin (no se mostrarán como chip activo)
    const systemAttachments = rawSystemAttachments.filter(a => !excludedDefaultPaths.includes(a.s3_path));
    const removedSystemAttachments = rawSystemAttachments.filter(a => excludedDefaultPaths.includes(a.s3_path));

    const handleFileSelect = (e) => {
        const added = Array.from(e.target.files || []);
        const updated = [...files, ...added];
        setFiles(updated);
        setCompanyUploadedFiles(updated);
        e.target.value = '';
    };

    const removeFile = (index) => {
        const updated = files.filter((_, i) => i !== index);
        setFiles(updated);
        setCompanyUploadedFiles(updated);
    };

    const addCcEmail = () => setCompanyCcEmails([...companyCcEmails, '']);
    const updateCcEmail = (index, value) => {
        const newCc = [...companyCcEmails];
        newCc[index] = value;
        setCompanyCcEmails(newCc);
    };
    const removeCcEmail = (index) => setCompanyCcEmails(companyCcEmails.filter((_, i) => i !== index));

    return (
        <div className="fk-root flex flex-column gap-3">

            {/* Card 1: Destinatario */}
            <div className="tk-table-card">
                {cardHeader('Destinatario y Copias — Empresa')}
                <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <AppTextField
                                label="Dirigido a (Nombre del destinatario)"
                                value={companyRecipientName}
                                onChange={(e) => setCompanyRecipientName(e.target.value)}
                                placeholder="Ej. Juan Pérez"
                                disabled={isReadOnly}
                                invalid={!!errors.companyRecipientName}
                                errorMessage="Campo requerido"
                                helperText='Aparecerá en el "Estimado(a) [Nombre]" del correo.'
                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <AppTextField
                                label="Correo del destinatario (Empresa)"
                                value={companyEmail}
                                onChange={(e) => setCompanyEmail(e.target.value)}
                                placeholder="ejemplo@empresa.com"
                                disabled={isReadOnly}
                                invalid={!!errors.companyEmail}
                                errorMessage="Campo requerido"
                                helperText="Empresa que realizará la visita ocular."
                            />
                        </div>
                    </div>

                    {/* CC */}
                    <div className="flex flex-column gap-2">
                        <div className="flex align-items-center justify-content-between">
                            <span className="fk-label" style={{ margin: 0 }}>CC — Copia a otros correos</span>
                            {!isReadOnly && (
                                <button
                                    type="button"
                                    onClick={addCcEmail}
                                    className="fk-btn fk-btn--secondary fk-btn--sm"
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                >
                                    <i className="pi pi-plus" style={{ fontSize: '0.7rem' }} />
                                    Agregar CC
                                </button>
                            )}
                        </div>
                        {companyCcEmails.map((ccEmail, index) => (
                            <div key={index} className="flex gap-2 align-items-center">
                                <div className="fk-input flex-1">
                                    <input
                                        className="p-inputtext"
                                        value={ccEmail}
                                        onChange={(e) => updateCcEmail(index, e.target.value)}
                                        placeholder="destinatario@ejemplo.com"
                                        disabled={isReadOnly}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                {!isReadOnly && (
                                    <button
                                        type="button"
                                        className="fk-icon-btn fk-icon-btn--danger"
                                        onClick={() => removeCcEmail(index)}
                                        title="Eliminar"
                                    >
                                        <i className="pi pi-times" style={{ fontSize: '0.8rem' }} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {companyCcEmails.length === 0 && (
                            <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                Sin destinatarios en copia
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Card 2: Correo */}
            <div className="tk-table-card">
                {cardHeader('Configuración del Correo')}
                <div style={{ padding: '1rem' }} className="flex flex-column gap-3">
                    <AppTextField
                        label="Asunto"
                        value={companyEmailSubject}
                        onChange={(e) => setCompanyEmailSubject(e.target.value)}
                        placeholder="Asunto del correo para la empresa"
                        disabled={isReadOnly}
                        invalid={!!errors.companyEmailSubject}
                        errorMessage="Campo requerido"
                    />
                    <AppTextareaField
                        label="Cuerpo del Mensaje"
                        value={companyEmailContent}
                        onChange={(e) => setCompanyEmailContent(e.target.value)}
                        rows={12}
                        placeholder="Escribe el mensaje para la empresa..."
                        readOnly={isReadOnly}
                        invalid={!!errors.companyEmailContent}
                        errorMessage="Campo requerido"
                        style={{ minHeight: '300px' }}
                    />

                </div>
            </div>

            {/* Documentos: nueva adjuntar + historial en una sola card */}
            {(
                !isReadOnly
                || rawSystemAttachments.length > 0
                || (stepData?.files && stepData.files.some(f => f.file_type?.startsWith('ocular_visit_company')))
            ) && (
                <div className="tk-table-card">
                    {cardHeader('Documentos')}
                    <div style={{ padding: '1rem' }} className="flex flex-column gap-3">

                        {/* Leyenda — solo si hay archivos del sistema */}
                        {!isReadOnly && rawSystemAttachments.length > 0 && (
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.35rem',
                                    color: '#475569',
                                    fontSize: '0.7rem',
                                    lineHeight: 1.2,
                                }}
                            >
                                <i className="pi pi-info-circle" style={{ fontSize: '0.7rem', color: '#2563eb' }} />
                                <span>Adjuntamos todos los comprobantes del cliente. Quita los que no apliquen.</span>
                            </div>
                        )}

                        {/* Adjuntar nuevos + Chips del Sistema (siempre visibles si hay default_attachments) */}
                        <div className="flex align-items-center gap-2 flex-wrap">
                            {!isReadOnly && (
                                <>
                                    <button
                                        type="button"
                                        className="fk-btn fk-btn--secondary fk-btn--sm"
                                        onClick={() => inputRef.current?.click()}
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}
                                    >
                                        <i className="pi pi-paperclip" style={{ fontSize: '0.72rem' }} />
                                        Adjuntar documentos
                                    </button>
                                    <input ref={inputRef} type="file" multiple accept="*/*" style={{ display: 'none' }} onChange={handleFileSelect} />
                                </>
                            )}

                            {/* Chips del Sistema (onboarding) — removibles por el admin */}
                            {systemAttachments.map((att, i) => (
                                <span
                                    key={`sys-${i}-${att.s3_path}`}
                                    title={`Adjunto automático del cliente${att.label ? ': ' + att.label : ''}`}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        background: '#eff6ff',
                                        border: '1px solid #bfdbfe',
                                        borderRadius: '99px',
                                        padding: '0.2rem 0.4rem 0.2rem 0.55rem',
                                        fontSize: '0.72rem',
                                        color: '#1e40af',
                                        fontWeight: 600,
                                        maxWidth: '320px',
                                        boxShadow: '0 1px 2px rgba(30, 64, 175, 0.06)',
                                    }}
                                >
                                    <i className="pi pi-shield" style={{ fontSize: '0.7rem', color: '#2563eb', flexShrink: 0 }} />
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        background: '#dbeafe',
                                        border: '1px solid #bfdbfe',
                                        color: '#1d4ed8',
                                        padding: '0.05rem 0.4rem',
                                        borderRadius: '99px',
                                        fontSize: '0.6rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        flexShrink: 0,
                                    }}>
                                        {att.label || 'Sistema'}
                                    </span>
                                    {att.s3_url ? (
                                        <button
                                            type="button"
                                            onClick={() => setPreviewAttachment(att)}
                                            title={`Vista previa: ${att.file_name}`}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                color: '#1e40af',
                                                textDecoration: 'none',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '160px',
                                                cursor: 'pointer',
                                                fontWeight: 600,
                                                fontSize: '0.72rem',
                                            }}
                                        >
                                            {att.file_name}
                                        </button>
                                    ) : (
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                                            {att.file_name}
                                        </span>
                                    )}
                                    {!isReadOnly && onToggleDefaultPath && (
                                        <button
                                            type="button"
                                            onClick={() => onToggleDefaultPath(att.s3_path)}
                                            title="Quitar este adjunto del correo"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                        >
                                            <i className="pi pi-times" style={{ fontSize: '0.62rem' }} />
                                        </button>
                                    )}
                                </span>
                            ))}

                            {/* Chips removidos — el admin puede restaurarlos */}
                            {!isReadOnly && removedSystemAttachments.map((att, i) => (
                                <span
                                    key={`sys-removed-${i}-${att.s3_path}`}
                                    title={`Removido — clic para volver a adjuntar${att.label ? ' (' + att.label + ')' : ''}`}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        background: '#f1f5f9',
                                        border: '1px dashed #cbd5e1',
                                        borderRadius: '99px',
                                        padding: '0.2rem 0.4rem 0.2rem 0.55rem',
                                        fontSize: '0.7rem',
                                        color: '#64748b',
                                        fontWeight: 500,
                                        maxWidth: '320px',
                                        textDecoration: 'line-through',
                                    }}
                                >
                                    <i className="pi pi-ban" style={{ fontSize: '0.7rem', color: '#94a3b8', flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#94a3b8', flexShrink: 0 }}>
                                        {att.label || 'Sistema'}
                                    </span>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                                        {att.file_name}
                                    </span>
                                    {onToggleDefaultPath && (
                                        <button
                                            type="button"
                                            onClick={() => onToggleDefaultPath(att.s3_path)}
                                            title="Restaurar este adjunto"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0, textDecoration: 'none' }}
                                        >
                                            <i className="pi pi-undo" style={{ fontSize: '0.62rem' }} />
                                        </button>
                                    )}
                                </span>
                            ))}

                            {!isReadOnly && files.length === 0 && rawSystemAttachments.length === 0 && (
                                <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Sin archivos adjuntos</span>
                            )}
                            {!isReadOnly && files.map((file, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '99px', padding: '0.2rem 0.4rem 0.2rem 0.65rem', fontSize: '0.73rem', color: '#334155', maxWidth: '200px' }}>
                                    <i className="pi pi-file" style={{ fontSize: '0.68rem', flexShrink: 0 }} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                                    <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 0.1rem', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                        <i className="pi pi-times" style={{ fontSize: '0.62rem' }} />
                                    </button>
                                </span>
                            ))}
                        </div>

                        {/* Historial */}
                        {stepData?.files && stepData.files.some(f => f.file_type?.startsWith('ocular_visit_company')) && (
                            <>
                                {!isReadOnly && <div style={{ height: '1px', background: '#e2e8f0' }} />}
                                {['scheduled', 'postponed', 'completed', 'paused', 'unsuccessful', 'legacy'].map(status => {
                                    const histFiles = stepData.files.filter(f =>
                                        status === 'legacy' ? f.file_type === 'ocular_visit_company' : f.file_type === `ocular_visit_company_${status}`
                                    );
                                    if (histFiles.length === 0) return null;
                                    const label = { scheduled: 'Programada', postponed: 'Reprogramada', paused: 'Pausada', completed: 'Completado', unsuccessful: 'No exitosa', legacy: 'Anteriores' }[status];
                                    return (
                                        <div key={status}>
                                            <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.25rem' }}>{label}</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {histFiles.map(file => (
                                                    <div key={file.id} className="flex align-items-center gap-2 p-2 border-1 border-gray-300 border-round bg-white shadow-1" style={{ minWidth: '200px' }}>
                                                        <i className={`pi ${file.file_name?.endsWith('.pdf') ? 'pi-file-pdf text-red-500' : 'pi-image text-blue-500'} text-xl`} />
                                                        <div className="flex flex-column" style={{ maxWidth: '160px' }}>
                                                            <span className="text-sm font-medium text-gray-800 white-space-nowrap overflow-hidden text-overflow-ellipsis" title={file.file_name}>{file.file_name}</span>
                                                            <span className="text-xs text-gray-500">{new Date(file.created_at).toLocaleDateString()}</span>
                                                        </div>
                                                        {file.s3_url && (
                                                            <a href={file.s3_url} target="_blank" rel="noopener noreferrer" className="ml-auto flex align-items-center p-1 hover:bg-gray-100 border-round">
                                                                <i className="pi pi-eye text-blue-600 cursor-pointer" />
                                                            </a>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal de Vista Previa de adjunto del Sistema */}
            <Dialog
                visible={!!previewAttachment}
                onHide={() => setPreviewAttachment(null)}
                modal
                dismissableMask
                header={previewAttachment?.file_name || 'Vista previa'}
                style={{ width: '90vw', maxWidth: '1100px', height: '88vh' }}
                contentStyle={{ padding: 0, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
                {previewAttachment?.s3_url ? (
                    <iframe
                        title={previewAttachment.file_name}
                        src={previewAttachment.s3_url}
                        style={{ flex: 1, width: '100%', border: 'none', background: '#f8fafc' }}
                    />
                ) : (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                        No hay vista previa disponible.
                    </div>
                )}
            </Dialog>

        </div>
    );
};
